import { Component, inject, OnInit, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgIconComponent } from '@ng-icons/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
	phosphorUser,
	phosphorUserGear,
	phosphorGear,
	phosphorPlus,
	phosphorX,
	phosphorSunDim,
	phosphorMoon
} from '@ng-icons/phosphor-icons/regular';
import { catchError, of } from 'rxjs';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { InputTextModule } from 'primeng/inputtext';
import { StudentsService } from '../../services/students.service';
import { DarkThemeService } from '../../services/dark-theme.service';
import { MessagesService } from '../../services/messages.service';
import { PillsResponse, StudentsResponse } from '../../models/api/students.model';
import { LoadingComponent } from '../loading/loading.component';

@Component({
	selector: 'unir-ai-user-preferences',
	standalone: true,
	imports: [
		CommonModule,
		NgIconComponent,
		FormsModule,
		InputTextareaModule,
		InputTextModule,
		LoadingComponent,
		TranslateModule
	],
	templateUrl: './user-preferences.component.html',
	styleUrl: './user-preferences.component.scss'
})
export class UserPreferencesComponent implements OnInit {
	readonly #messageService = inject(MessagesService);

	icons: Record<string, string> = {
		customize: phosphorUserGear,
		preferences: phosphorGear,
		user: phosphorUser,
		plus: phosphorPlus,
		close: phosphorX,
		light: phosphorSunDim,
		dark: phosphorMoon
	};

	nickModel = '';
	qualitiesModel = '';

	maxCharNick = 30;
	maxCharQualities = 250;

	darkThemeService = inject(DarkThemeService);
	translate = inject(TranslateService);

	private studentService = inject(StudentsService);

	closeUserPreferences = output<void>();

	showPreferences = signal(false);

	loaded = signal(false);

	student = signal<StudentsResponse | undefined>(undefined);
	pills = signal<PillsResponse[] | undefined>(undefined);

	languages = [
		{ code: 'es_ES', name: 'Español' },
		{ code: 'en_EN', name: 'English' }
	];
	private currentLang = '';

	ngOnInit() {
		this.getStudent();
		this.getPills();
	}

	getStudent() {
		this.studentService
			.getStudents()
			.pipe(
				catchError(() => {
					this.#messageService.sendMessageTranslate({
						type: 'error',
						title: 'ERRORS.PREFERENCES.GET',
						text: 'ERRORS.CLOSE_RETRY',
						redirect: true
					});
					this.onCancel();
					return of(undefined);
				})
			)
			.subscribe((student) => {
				if (student !== undefined) {
					this.student.set(student);
					this.nickModel = student?.nickname ?? '';
					this.qualitiesModel = student?.desiredTraits ?? '';
					this.currentLang = student?.language ? student.language.replace('-', '_') : this.translate.currentLang;
					this.loaded.set(true);
				}
			});
	}

	getPills() {
		this.studentService
			.getPills(this.translate.currentLang.replace('_', '-'))
			.pipe(
				catchError(() => {
					return of(undefined);
				})
			)
			.subscribe((pills) => {
				if (pills !== undefined) {
					this.pills.set(pills);
				}
			});
	}

	onPillClick(description: string) {
		const qualities = this.qualitiesModel.trim().length === 0 ? description : this.qualitiesModel + ' ' + description;

		this.qualitiesModel =
			qualities.length <= this.maxCharQualities ? qualities : qualities.slice(0, this.maxCharQualities);
	}

	onCancel() {
		this.darkThemeService.checkThemeMode();
		if (this.translate.currentLang !== this.currentLang) {
			this.translate.use(this.currentLang);
		}
		this.closeUserPreferences.emit();
	}

	onSave() {
		const student = {
			nickname: this.nickModel,
			desiredTraits: this.qualitiesModel,
			darkMode: this.darkThemeService.isDarkTheme(),
			language: this.translate.currentLang
		};
		this.studentService
			.updateStudents(student)
			.pipe(
				catchError(() => {
					this.#messageService.sendMessageTranslate({
						type: 'error',
						title: 'ERRORS.PREFERENCES.SAVE',
						text: 'ERRORS.CLOSE_RETRY'
					});
					return of(undefined);
				})
			)
			.subscribe((saved) => {
				if (saved !== undefined) {
					this.#messageService.sendMessageTranslate({
						type: 'success',
						title: 'PREFERENCES.SAVED',
						text: 'PREFERENCES.SETTINGS_SAVED'
					});
				}
				this.closeUserPreferences.emit();
			});
	}

	changeLanguage(lang: string) {
		this.translate.use(lang);
	}
}
